import {
  BaseClient,
  BaseClientOptions,
  EquestrainResponse,
} from "@/shared-types/client/base";
import { CustomTypes, StripeTypes } from "@/shared-types/v1";
import {
  Club,
  FilterGroup,
  Gym,
  HomeWorkout,
  LibraryAsset,
  MobilityWorkout,
  Postride,
  Preride,
  VideoAsset,
} from "@/shared-types/v2";
import {
  CardioCategory,
  CardioWorkout,
} from "@/shared-types/v2/workouts/cardio";
import { Asset, PlaybackId, Upload } from "@mux/mux-node";
import {
  DeleteUsersResult,
  ListUsersResult,
  UserRecord,
} from "firebase-admin/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { Customer, Subscriber } from "./equestrain-admin-types";

/** Client for fetching and modifying Equestrain ADMIN related data.
 *
 * The client makes most requests to the Equestrain API (Firebase Functions)
 * but some requests may be made to the Equestrain database (in firebase) directly.
 */
export class Equestrain {
  Legacy: LegacyClient;
  Auth: AuthClient;
  Client: Client;
  constructor(options: BaseClientOptions) {
    this.Legacy = new LegacyClient(options);
    this.Client = new Client(options);
    this.Auth = new AuthClient(options);
  }
} // class end

class AuthClient extends BaseClient {
  login = async (email: string, password: string) => {
    this.onDataLoadingStart();
    const { user } = await signInWithEmailAndPassword(
      this.firebaseAuth,
      email,
      password,
    );
    const t = await user.getIdTokenResult();
    if (!t.claims.admin) {
      this.onDataLoadingEnd();
      throw new Error("User is not admin");
    }
    this.onDataLoadingEnd();
    return user;
  };

  logout = async () => {
    this.onDataLoadingStart();
    await this.firebaseAuth.signOut();
    this.onDataLoadingEnd();
  };

  isLoggedIn = () => {
    if (this.firebaseAuth.currentUser !== null) {
      return true;
    }
    return false;
  };
}

class Client extends BaseClient {
  // EXAMPLE FOR FETCHING DATA DIRECTLY FROM FIREBASE REALTIME DATABASE
  // getAllVideos = async () => {
  //   const { snapshot, error } = await this.fetchSnapshot("videos");
  //   if (error) {
  //     return this.formatData(null, error);
  //   }
  //   if (snapshot.exists()) {
  //     const ar: VideoAsset[] = Object.values(snapshot.val());
  //     return this.formatData(ar, null);
  //   }
  //   return this.formatData(null, new Error("No data found"));
  // };

  getAllVideos = async (includeOrphans?: boolean) => {
    let url = "";
    if (includeOrphans) {
      url = "/api/v1/admin/videos?filter=all&orphans=true";
    } else {
      url = "/api/v1/admin/videos?filter=all";
    }
    return this.fetch<CustomTypes.PagedResponse<VideoAsset>>(url);
  };
  /** returns VideoAsset but gets muxAsset directly from Mux and does not include any firebase data */
  getMuxOnlyVideo = async (muxAssetId: string) => {
    return this.fetch<VideoAsset>(
      "api/v1/admin/videos?muxAssetId=" + muxAssetId,
    );
  };
  editVideo = async (video: VideoAsset) => {
    return this.fetch<VideoAsset>("/api/v1/admin/videos", {
      method: "POST",
      body: JSON.stringify(video),
    });
  };
  removeVideos = async (ids: string[], hardDelete?: boolean) => {
    let url = `/api/v1/admin/videos`;
    if (hardDelete) {
      url = url + "?hardDelete=true";
    }
    return this.fetch<{ ids: string[] }>(url, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };

  watchVideo = async (video: VideoAsset) => {
    if (
      !video.muxAsset.playback_ids ||
      video.muxAsset.playback_ids.length === 0
    ) {
      return {
        data: null,
        error: new Error("No playback ids found for video"),
      } as EquestrainResponse<null>;
    }
    // always pick first as we don't use multiple playback ids
    const playbackId = video.muxAsset.playback_ids[0];
    // in public policy we dont need to make a request to the server to get the tokenized url
    if (playbackId.policy === "public") {
      return {
        // TODO: Put stream.mux.com in a config file
        data: `https://stream.mux.com/${playbackId.id}.m3u8`,
        error: null,
      } as EquestrainResponse<string>;
    }
    // request to server is responded with a redirect containing the tokenized url
    const res = await this.fetch<string>(
      `/api/v1/admin/watch?playbackId=${playbackId.id}`,
    );
    if (res.nativeResponse?.redirected) {
      return {
        data: res.nativeResponse.url,
        error: null,
        statusCode: res.statusCode,
        nativeResponse: res.nativeResponse,
      } as EquestrainResponse<string>;
    }
    return {
      data: null,
      error: new Error("Could not watch video"),
    } as EquestrainResponse<null>;
  };
  getTopics = async () => {
    return this.fetch<Club.Topic[]>("/api/v1/admin/club/topics");
  };
  editTopic = async (topic: Club.Topic) => {
    return this.fetch<Club.Topic>("/api/v1/admin/club/topics", {
      method: "POST",
      body: JSON.stringify(topic),
    });
  };
  removeTopics = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(`/api/v1/admin/club/topics`, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };

  getCategories = async () => {
    return this.fetch<Club.Category[]>("/api/v1/admin/club/categories");
  };
  editCategory = async (category: Club.Category) => {
    return this.fetch<Club.Category>("/api/v1/admin/club/categories", {
      method: "POST",
      body: JSON.stringify(category),
    });
  };
  removeCategory = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(`/api/v1/admin/club/categories`, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };

  getPrerides = async () => {
    return this.fetch<Preride.Preride[]>("/api/v1/admin/workouts/preride");
  };
  editPreride = async (preride: Preride.Preride) => {
    return this.fetch<Preride.Preride>("/api/v1/admin/workouts/preride", {
      method: "POST",
      body: JSON.stringify(preride),
    });
  };
  removePrerides = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(`/api/v1/admin/workouts/preride`, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };
  getPostrides = async () => {
    return this.fetch<Postride.Postride[]>("/api/v1/admin/workouts/postride");
  };
  editPostride = async (postride: Postride.Postride) => {
    return this.fetch<Postride.Postride>("/api/v1/admin/workouts/postride", {
      method: "POST",
      body: JSON.stringify(postride),
    });
  };
  removePostrides = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(`/api/v1/admin/workouts/postride`, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };
  // home workouts
  getHomeWorkouts = async () => {
    return this.fetch<HomeWorkout.HomeWorkout[]>(
      "/api/v1/admin/workouts/homeworkout",
    );
  };
  editHomeWorkout = async (homeWorkout: HomeWorkout.HomeWorkout) => {
    return this.fetch<HomeWorkout.HomeWorkout>(
      "/api/v1/admin/workouts/homeworkout",
      {
        method: "POST",
        body: JSON.stringify(homeWorkout),
      },
    );
  };
  removeHomeWorkouts = async (ids: string[], hardDelete?: boolean) => {
    let url = `/api/v1/admin/workouts/homeworkout`;
    if (hardDelete) {
      url = url + "?hardDelete=true";
    }
    return this.fetch<{ success: boolean }>(url, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };
  // mobility workouts
  getMobilityWorkouts = async () => {
    return this.fetch<MobilityWorkout.MobilityWorkout[]>(
      "/api/v1/admin/workouts/mobilityworkout",
    );
  };
  editMobilityWorkout = async (
    mobilityWorkout: MobilityWorkout.MobilityWorkout,
  ) => {
    return this.fetch<MobilityWorkout.MobilityWorkout>(
      "/api/v1/admin/workouts/mobilityworkout",
      {
        method: "POST",
        body: JSON.stringify(mobilityWorkout),
      },
    );
  };
  removeMobilityWorkouts = async (ids: string[], hardDelete?: boolean) => {
    let url = `/api/v1/admin/workouts/mobilityworkout`;
    if (hardDelete) {
      url = url + "?hardDelete=true";
    }
    return this.fetch<{ success: boolean }>(url, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };
  // Gym stuff
  getWarmups = async () => {
    return this.fetch<Gym.Warmup[]>("/api/v1/admin/workouts/gym/warmup");
  };
  editWarmup = async (warmup: Gym.Warmup) => {
    return this.fetch<Gym.Warmup>("/api/v1/admin/workouts/gym/warmup", {
      method: "POST",
      body: JSON.stringify(warmup),
    });
  };
  removeWarmups = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/gym/warmup`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };
  // SpeedAndCoordination handlers
  getSpeedAndCoordination = async () => {
    return this.fetch<Gym.SpeedAndCoordination[]>(
      "/api/v1/admin/workouts/gym/speed-and-coordination",
    );
  };
  editSpeedAndCoordination = async (
    speedAndCoordination: Gym.SpeedAndCoordination,
  ) => {
    return this.fetch<Gym.SpeedAndCoordination>(
      "/api/v1/admin/workouts/gym/speed-and-coordination",
      {
        method: "POST",
        body: JSON.stringify(speedAndCoordination),
      },
    );
  };
  removeSpeedAndCoordination = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/gym/speed-and-coordination`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };

  // StrengthAndAccessory handlers
  getStrengthAndAccessory = async () => {
    return this.fetch<Gym.StrengthAndAccessory[]>(
      "/api/v1/admin/workouts/gym/strength-and-accessory",
    );
  };
  editStrengthAndAccessory = async (
    strengthAndAccessory: Gym.StrengthAndAccessory,
  ) => {
    return this.fetch<Gym.StrengthAndAccessory>(
      "/api/v1/admin/workouts/gym/strength-and-accessory",
      {
        method: "POST",
        body: JSON.stringify(strengthAndAccessory),
      },
    );
  };
  removeStrengthAndAccessory = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/gym/strength-and-accessory`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };

  // CircuitWorkout handlers
  getCircuitWorkouts = async () => {
    return this.fetch<Gym.CircuitWorkout[]>(
      "/api/v1/admin/workouts/gym/circuit",
    );
  };
  editCircuitWorkout = async (circuitWorkout: Gym.CircuitWorkout) => {
    return this.fetch<Gym.CircuitWorkout>(
      "/api/v1/admin/workouts/gym/circuit",
      {
        method: "POST",
        body: JSON.stringify(circuitWorkout),
      },
    );
  };
  removeCircuitWorkouts = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/gym/circuit`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };

  // CardioWorkout handlers
  getCardioWorkouts = async () => {
    return this.fetch<CardioWorkout[]>("/api/v1/admin/workouts/gym/cardio");
  };
  editCardioWorkout = async (cardioWorkout: CardioWorkout) => {
    return this.fetch<CardioWorkout>("/api/v1/admin/workouts/gym/cardio", {
      method: "POST",
      body: JSON.stringify(cardioWorkout),
    });
  };
  removeCardioWorkouts = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/gym/cardio`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };

  // CardioWorkout V2 handlers (categorized cardio, stored under workouts/cardiov2)
  getCardioWorkoutsV2 = async () => {
    return this.fetch<CardioWorkout[]>("/api/v1/admin/workouts/cardio/cardios");
  };
  editCardioWorkoutV2 = async (cardioWorkout: CardioWorkout) => {
    return this.fetch<CardioWorkout>("/api/v1/admin/workouts/cardio/cardios", {
      method: "POST",
      body: JSON.stringify(cardioWorkout),
    });
  };
  removeCardioWorkoutsV2 = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/cardio/cardios`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };

  // CardioCategory handlers (stored under workouts/cardiocategories)
  getCardioCategories = async () => {
    return this.fetch<CardioCategory[]>(
      "/api/v1/admin/workouts/cardio/categories",
    );
  };
  editCardioCategory = async (cardioCategory: CardioCategory) => {
    return this.fetch<CardioCategory>(
      "/api/v1/admin/workouts/cardio/categories",
      {
        method: "POST",
        body: JSON.stringify(cardioCategory),
      },
    );
  };
  removeCardioCategories = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/workouts/cardio/categories`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };
  // library stuff
  getLibraryAssets = async () => {
    return this.fetch<LibraryAsset[]>("/api/v1/admin/library/assets");
  };
  editLibraryAsset = async (asset: LibraryAsset) => {
    return this.fetch<LibraryAsset>("/api/v1/admin/library/assets", {
      method: "POST",
      body: JSON.stringify(asset),
    });
  };
  createLibraryAssetsFromVideos = async (ids: string[]) => {
    return this.fetch<LibraryAsset[]>(
      "/api/v1/admin/library/assets?fromVideoAssets=true",
      {
        method: "POST",
        body: JSON.stringify(ids),
      },
    );
  };
  removeLibraryAssets = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(`/api/v1/admin/library/assets`, {
      method: "DELETE",
      body: JSON.stringify(ids),
    });
  };
  getFilterGroups = async () => {
    return this.fetch<FilterGroup[]>("/api/v1/admin/library/filtergroups");
  };
  editFilterGroups = async (fg: FilterGroup) => {
    return this.fetch<FilterGroup>("/api/v1/admin/library/filtergroups", {
      method: "POST",
      body: JSON.stringify(fg),
    });
  };
  removeFilterGroups = async (ids: string[]) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/library/filtergroups`,
      {
        method: "DELETE",
        body: JSON.stringify(ids),
      },
    );
  };
}

/** Legacy client contains functions related to Equestrain V1 and V2 */
class LegacyClient extends BaseClient {
  // Legacy code imported from old AuthProvider.tsx
  getOrCreateSubscriber = async (
    uid: string,
  ): Promise<EquestrainResponse<Subscriber>> => {
    return this.fetch<Subscriber>(`/api/v1/admin/subscriber?&id=${uid}`);
  };
  /// CAN BE REMOVED PROBABLY SINCE THIS IS NOT USED IN ADMIN PANEL AT ALL
  // mapUserAndStripeCustomer = async (userId: string, customerId: string) => {
  //   await this.fetch(
  //     `/api/v1/admin/mappings/user?&userId=${userId}&customerId=${customerId}`,
  //     { method: "POST" }
  //   );
  // };
  getEventLogs = async (): Promise<
    EquestrainResponse<CustomTypes.EventLog[]>
  > => {
    return this.fetch<CustomTypes.EventLog[]>("/api/v1/admin/eventlog");
  };
  getStripeProducts = async (): Promise<
    EquestrainResponse<StripeTypes.ProductWithPrice[]>
  > => {
    return this.fetch<StripeTypes.ProductWithPrice[]>(
      "/api/v1/stripe/products",
    );
  };
  getStripeCustomer = async (
    email: string,
  ): Promise<EquestrainResponse<Customer>> => {
    return this.fetch<Customer>("/api/v1/stripe/customer?&email=" + email);
  };
  postStripeReceiptToRevenueCat = async (
    userId: string,
    receipt: { checkoutSessionId?: string; subscriptionId?: string },
  ) => {
    const { subscriptionId } = receipt;
    const res = await fetch("https://api.revenuecat.com/v1/receipts", {
      method: "POST",
      headers: {
        Authorization: "Bearer strp_bKJGooJDHqEYYhWqQWccOQdFsAJ",
        "Content-Type": "application/json",
        "X-Platform": "stripe",
      },
      body: JSON.stringify({
        app_user_id: userId,
        fetch_token: subscriptionId,
      }),
    });

    if (res.status === 200) {
      const data = await res.json();
      return data;
    }
    return null;
  };
  postPaddleReceiptToRevenueCat = async (
    userId: string,
    receipt: { subscriptionId?: string },
  ) => {
    const { subscriptionId } = receipt;
    const res = await fetch("https://api.revenuecat.com/v1/receipts", {
      method: "POST",
      headers: {
        Authorization: "Bearer pdl_NPmSxsReIBhpagcQNahrbDewgxCJ",
        "Content-Type": "application/json",
        "X-Platform": "paddle",
      },
      body: JSON.stringify({
        app_user_id: userId,
        fetch_token: subscriptionId,
      }),
    });
    console.log(res);
    if (res.status === 200) {
      const data = await res.json();
      return data;
    }
    return null;
  };
  getAvailableProducts = async (): Promise<
    EquestrainResponse<StripeTypes.ProductWithPrice[]>
  > => {
    return this.fetch<StripeTypes.ProductWithPrice[]>("/api/v1/admin/products");
  };
  editAvailableProducts = async (products: StripeTypes.ProductWithPrice[]) => {
    return this.fetch<StripeTypes.ProductWithPrice[]>(
      "/api/v1/admin/products",
      {
        method: "POST",
        body: JSON.stringify(products),
      },
    );
  };
  getAppSettings = async (): Promise<
    EquestrainResponse<CustomTypes.DynamicAppSettings>
  > => {
    return this.fetch<CustomTypes.DynamicAppSettings>(
      "/api/v1/admin/dynamicsettings",
    );
  };

  editAppSettings = async (settings: CustomTypes.DynamicAppSettings) => {
    return this.fetch<CustomTypes.DynamicAppSettings>(
      "/api/v1/admin/dynamicsettings",
      {
        method: "POST",
        body: JSON.stringify(settings),
      },
    );
  };
  removeUsers = async (userIds: string[]) => {
    return this.fetch<DeleteUsersResult>("/api/v1/admin/users", {
      method: "DELETE",
      body: JSON.stringify({ userIds: userIds }),
    });
  };
  editUser = async (user: CustomTypes.PartialUserRecord, id?: string) => {
    let addr = `/api/v1/admin/users`;
    if (id) {
      addr = addr + `?id=${id}`;
    }
    return this.fetch<UserRecord>(addr, {
      method: "POST",
      body: JSON.stringify(user),
    });
  };
  getUsers = async (maxResults?: number, pageToken?: string) => {
    let addr = `/api/v1/admin/users`;
    if (maxResults || pageToken) {
      addr = addr + `?`;
      if (maxResults) {
        addr = addr + `&maxResults=${maxResults}`;
      }
      if (pageToken) {
        addr = addr + `&pageToken=${pageToken}`;
      }
    }
    return this.fetch<ListUsersResult>(addr);
  };
  searchUsers = async (searchValue: string) => {
    let addr = `/api/v1/admin/users`;
    if (searchValue) {
      addr = addr + `?&search=${searchValue}`;
    }
    return this.fetch<UserRecord[]>(addr);
  };
  updatePlaybackPolicy = async (id: string, policy: string) => {
    let addr = `/api/v1/admin/playback-policy?id=` + id + `&policy=` + policy;
    return this.fetch<PlaybackId>(addr, {
      method: "POST",
    });
  };
  removeAsset = async (id: string) => {
    return this.fetch<{
      success: boolean;
    }>(`/api/v1/admin/asset?id=${id}`, {
      method: "DELETE",
    });
  };
  editAsset = async (id: string, assetInfo: CustomTypes.AssetInfo) => {
    return this.fetch<CustomTypes.AssetInfo>(`/api/v1/admin/assets?id=${id}`, {
      method: "POST",
      body: JSON.stringify(assetInfo),
    });
  };
  getAssets = async (opts?: {
    nextPageToken?: string | undefined;
    limit?: number;
  }) => {
    const { nextPageToken, limit } = opts || {};
    let addr = `/api/v1/admin/assets?filter=all`;
    if (limit) {
      addr = addr + `&limit=${limit}`;
    }
    if (nextPageToken) {
      addr = addr + `&startAfter=${nextPageToken}`;
    }
    return this.fetch<CustomTypes.PagedResponse<CustomTypes.Asset>>(addr);
  };
  getAssetsMetadataOnly = async () => {
    const extractMetadata = (asset: CustomTypes.Asset) => {
      return {
        ...asset.assetInfo,
        id: asset.id,
      } as CustomTypes.AssetInfo;
    };
    // fetch all assets and return only metadata
    const limit = 100;
    let nextPageToken: string | undefined = undefined;
    const res = await this.getAssets({ limit });
    if (!res.data || res.error) {
      return {
        data: null,
        error: res.error,
        statusCode: res.statusCode,
        nativeResponse: res.nativeResponse,
      } as EquestrainResponse<null>;
    }
    const initialResponse = res.data;
    let responses = initialResponse.data.map(extractMetadata);
    nextPageToken = initialResponse.nextPageToken;
    try {
      while (nextPageToken !== undefined) {
        const response = await this.getAssets({ limit, nextPageToken });
        if (response.error) {
          return {
            data: responses as CustomTypes.AssetInfo[],
            error: new Error(
              "could not fetch all data: " + response.error.message,
            ),
            statusCode: response.statusCode,
            nativeResponse: null,
          } as EquestrainResponse<CustomTypes.AssetInfo[]>;
        }
        const pagedResponse = response.data;
        if (pagedResponse) {
          const rows = pagedResponse.data.map(extractMetadata);
          responses.concat(rows);
          nextPageToken = pagedResponse.nextPageToken;
        } else {
          nextPageToken = undefined;
        }
      }
      return {
        data: responses as CustomTypes.AssetInfo[],
        error: null,
        statusCode: 200,
        nativeResponse: null,
      } as EquestrainResponse<CustomTypes.AssetInfo[]>;
    } catch (error: any) {
      throw new Error(error);
    }
  };
  searchAssets = async (searchValue: string) => {
    let addr = `/api/v1/admin/assets`;
    if (searchValue) {
      addr = addr + `?&search=${searchValue}`;
    }
    return this.fetch<CustomTypes.Asset[]>(addr);
  };
  getAsset = async (id: string) => {
    let addr = `/api/v1/admin/assets?id=${id}`;
    return this.fetch<CustomTypes.Asset>(addr);
  };
  checkVideoUpload = async (id: string) => {
    let addr = `/api/v1/admin/uploads?id=${id}`;
    return this.fetch<Upload>(addr);
  };
  uploadVideoFromUrl = async (url: string, playbackPolicy?: string) => {
    let addr = `/api/v1/admin/uploads?url=${url}`;
    if (playbackPolicy) {
      addr = addr + `&playback_policy=${playbackPolicy}`;
    }
    return this.fetch<Asset>(addr, {
      method: "POST",
    });
  };
  createVideoUpload = async (playbackPolicy?: string) => {
    let addr = `/api/v1/admin/uploads`;
    if (playbackPolicy) {
      addr = addr + `?playback_policy=${playbackPolicy}`;
    }
    return this.fetch<Upload>(addr, {
      method: "POST",
    });
  };
  editCategory = async (collection: CustomTypes.AssetCategory, id?: string) => {
    let addr = `/api/v1/admin/categories`;
    if (id) {
      addr = addr + `?id=` + id;
    }
    return this.fetch<CustomTypes.AssetCategory>(addr, {
      method: "POST",
      body: JSON.stringify(collection),
    });
  };
  removeCategory = async (id: string) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/categories?id=${id}`,
      {
        method: "DELETE",
      },
    );
  };

  getCategories = async () => {
    return this.fetch<CustomTypes.AssetCategory[]>("/api/v1/admin/categories");
  };

  editCollection = async (collection: CustomTypes.Collection, id?: string) => {
    let addr = `/api/v1/admin/collections`;
    if (id) {
      addr = addr + `?id=` + id;
    }
    return this.fetch<CustomTypes.Collection>(addr, {
      method: "POST",
      body: JSON.stringify(collection),
    });
  };

  removeCollection = async (id: string) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/collections?id=${id}`,
      {
        method: "DELETE",
      },
    );
  };

  getCollections = async () => {
    return this.fetch<CustomTypes.Collection[]>("/api/v1/admin/collections");
  };

  editDailyTip = async (dailyTip: CustomTypes.DailyTip, id?: string) => {
    let addr = `/api/v1/admin/dailytips`;
    if (id) {
      addr = addr + `?id=` + id;
    }
    return this.fetch<CustomTypes.DailyTip>(addr, {
      method: "POST",
      body: JSON.stringify(dailyTip),
    });
  };
  removeDailyTip = async (id: string) => {
    return this.fetch<{ success: boolean }>(
      `/api/v1/admin/dailytips?id=${id}`,
      {
        method: "DELETE",
      },
    );
  };
  getDailyTips = async () => {
    return this.fetch<CustomTypes.DailyTip[]>("/api/v1/admin/dailytips");
  };

  editProgram = async (program: CustomTypes.Program, id?: string) => {
    let addr = `/api/v1/admin/programs`;
    if (id) {
      addr = addr + `?id=` + id;
    }
    return this.fetch<CustomTypes.Program>(addr, {
      method: "POST",
      body: JSON.stringify(program),
    });
  };
  removeProgram = async (id: string) => {
    return this.fetch<{ success: boolean }>(`/api/v1/admin/programs?id=${id}`, {
      method: "DELETE",
    });
  };
  getPrograms = async () => {
    return this.fetch<CustomTypes.Program[]>("/api/v1/admin/programs");
  };
  /** this is a bulk operation that is run on the server-side and initiated by calling this function */
  updateVideoAssetMetadata = async () => {
    return this.fetch<string>("/api/v1/admin/assetmetadataupdate");
  };
  /**
   * Bulk operation, run server-side: re-reads every asset from Mux and replaces
   * each /videos/<id>/muxAsset. Repairs videos saved before Mux finished
   * processing, which have no duration (EQ-361).
   *
   * Long-running: the response is sent only once every write has landed, and
   * reports how many videos were actually refreshed. Fields outside muxAsset
   * (title, tags, keyPoints) are never touched, so re-running is safe.
   */
  refreshVideoMuxAssets = async () => {
    return this.fetch<string>("/api/v1/admin/assetmetadataupdatev2");
  };
  /** this is a bulk operation that is run on the server-side and initiated by calling this function */
  updateEmailsInRevenueCat = async () => {
    return this.fetch<string>("/api/v1/admin/attrupdate");
  };
}

export default Equestrain;
